import { h, JSX, Ref } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { OmnichannelVideoConfig } from '../../omnichannel-video/creative/config';
import { Asset } from '../types';
import { useAdParameters } from './hooks/useAdParameters';
import { useOptimizedAsset } from './hooks/useOptimizedAsset';

const imageStyle = {
  objectFit: 'contain',
  width: '100%',
  maxHeight: '100%',
};

interface ImageCropWrapperProps extends JSX.HTMLAttributes<HTMLDivElement> {
  containerRef?: Ref<HTMLDivElement>;
  asset: Asset | undefined;
  alt?: string;
  style?: JSX.CSSProperties;

  imageProps?: JSX.HTMLAttributes<HTMLImageElement>;
}

function checkMounted(mounted: { value: boolean }) {
  return (res: any) => {
    if (mounted.value) return res;
    else throw new Error('Component not mounted');
  };
}

const cropImage = (
  asset: Asset,
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D
): Promise<string> => {
  const x = asset.crop?.rect.x ?? 0;
  const y = asset.crop?.rect.y ?? 0;
  const w = asset.crop?.rect.w ?? image.naturalWidth;
  const h = asset.crop?.rect.h ?? image.naturalHeight;

  canvas.width = w;
  canvas.height = h;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, -x, -y);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('Error during blob creation'))),
      'image/png'
    );
  });
};

async function loadImage(src: string) {
  const img = document.createElement('img');
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject());
  });

  img.crossOrigin = 'anonymous';
  img.src = src;

  return p;
}

export function ImageCropWrapper({
  containerRef,
  asset,
  className,
  alt = 'unknown',
  style,
  imageProps,
  onClick,
  ...divProps
}: ImageCropWrapperProps) {
  const { assetStatus, preview, env } = useAdParameters<OmnichannelVideoConfig>();

  const assetId = asset?.id ?? '';
  const isAssetCropped = typeof assetStatus[assetId] === 'undefined' || assetStatus[assetId]?.cropPerformed;

  const optimizedAsset = useOptimizedAsset(asset);

  const originalAsset = useMemo(() => {
    if (!asset?.crop?.originalAsset) return '';
    const prefix = preview ? env.asset_bucket_uri : env.asset_cdn_uri;
    const baseUri = asset?.crop?.originalAsset?.startsWith('data:') ? '' : prefix ? `${prefix}/` : '';

    const cacheBuster = asset?.cacheBuster ?? asset?.time;
    const query = cacheBuster ? `?${cacheBuster}` : '';
    return `${baseUri}${asset.crop.originalAsset}${query}`;
  }, [asset?.crop?.originalAsset]);

  const canvas = useMemo(() => document.createElement('canvas'), []);
  const ctx = useMemo(() => canvas.getContext('2d')!, [canvas]);

  const isPlaceholder = asset?.uri?.includes('svg');

  useEffect(() => {
    if (isAssetCropped || !originalAsset || isPlaceholder || !asset) return;
    let mounted = { value: true };
    (async () => {
      const img = await loadImage(originalAsset).then(checkMounted(mounted));
      const src = await cropImage(asset, img, canvas, ctx).then(checkMounted(mounted));
      setCroppedImage(src);
    })().catch((e) => console.error(e));
    return () => (mounted.value = false);
  }, [isAssetCropped, originalAsset, asset?.crop]);

  const [croppedImage, setCroppedImage] = useState<string>('');

  useEffect(() => () => croppedImage && URL.revokeObjectURL(croppedImage), [croppedImage]);

  const assetSrc = useMemo(() => {
    if (isPlaceholder) return asset?.uri;
    if (isAssetCropped) return optimizedAsset;
    if (!isAssetCropped) return croppedImage;
  }, [isPlaceholder, isAssetCropped, asset?.uri, optimizedAsset, croppedImage]);
  const containerStyle = useMemo(
    () => ({
      ...style,
      '--src': `url(${assetSrc})`,
    }),
    [style, assetSrc]
  );

  const showImage = (isAssetCropped ? !!optimizedAsset : !!croppedImage) || isPlaceholder;

  return (
    <div {...divProps} className={className} style={containerStyle} ref={containerRef} onClick={onClick}>
      {showImage && <img {...imageProps} src={assetSrc} alt={alt} style={imageStyle} />}
    </div>
  );
}
